/**
 * DeviceCard - 设备卡片组件
 *
 * @example
 * // 使用示范
 * <DeviceCard
 *   deviceName="品牌名 Z7"
 *   deviceType="mouse"
 *   colorLibrary={{
 *     black: mouseBlackImage,
 *     silvery: mouseSilveryImage,
 *     red: mouseRedImage,
 *     purple: mousePurpleImage,
 *     blue: mouseBlueImage,
 *     gold: mouseGoldImage
 *   }}
 *   batteryLevel={75}
 *   connectionStatus="有线连接"
 *   onClick={() => handleClick()}
 * />
 */

import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DeviceType as DeviceTypeEnum } from "@eweadn-qiankun/shared-utils";
import { BatteryLevel } from "../BatteryLevel";
import { Iconfont } from "../Iconfont";
import "@eweadn-qiankun/shared-theme/color-dot.css";
import styles from "./DeviceCard.module.css";

export type DeviceType = DeviceTypeEnum;
export type DeviceColor = "black" | "silvery" | "red" | "purple";

export interface DeviceCardProps {
  /** 设备名称 */
  deviceName: string;
  /** 设备类型 */
  deviceType: DeviceType;
  /** 设备颜色库：键为颜色名，值为图片 URL；或单个图片 URL */
  colorLibrary: Record<string, string> | string;
  /** 电量百分比 (0-100) - 仅鼠标显示 */
  batteryLevel?: number;
  /** 是否正在充电 - 仅鼠标显示 */
  charging?: boolean;
  /** 连接状态 */
  connectionStatus?: string;
  /** 点击事件 */
  onClick?: () => void;
  /** 自定义类名 */
  className?: string;
  /** 默认选中的颜色 */
  defaultColor?: string;
  /** 外部控制的选中颜色（可选） */
  selectedColor?: string;
  /** 颜色变化回调（可选） */
  onColorChange?: (color: string) => void;
  /** 设备是否休眠 */
  sleeping?: boolean;
  /** 设备是否禁用 */
  disabled?: boolean;
}

const DeviceCard: React.FC<DeviceCardProps> = ({
  deviceName,
  deviceType: _deviceType,
  colorLibrary,
  batteryLevel,
  charging = false,
  connectionStatus,
  onClick,
  className,
  defaultColor,
  selectedColor: externalSelectedColor,
  onColorChange,
  sleeping = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  // 如果是对象,自动提取颜色列表;否则为空
  const availableColors =
    typeof colorLibrary === "object" ? Object.keys(colorLibrary) : [];

  // 默认颜色为第一个颜色或用户指定的颜色
  const initialColor =
    externalSelectedColor ||
    defaultColor ||
    (availableColors.length > 0 ? availableColors[0] : "");
  const [internalSelectedColor, setInternalSelectedColor] =
    useState<string>(initialColor);

  // 用于管理悬浮延迟的定时器
  const hoverTimerRef = useRef<number | null>(null);

  // 使用外部控制的颜色或内部状态
  const selectedColor =
    externalSelectedColor !== undefined
      ? externalSelectedColor
      : internalSelectedColor;

  // 当外部 selectedColor 变化时，同步内部状态
  useEffect(() => {
    if (externalSelectedColor !== undefined) {
      setInternalSelectedColor(externalSelectedColor);
    }
  }, [externalSelectedColor]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  // 如果 colorLibrary 是对象，根据 selectedColor 选择图片；否则直接使用
  const deviceImage =
    typeof colorLibrary === "string"
      ? colorLibrary
      : colorLibrary[selectedColor] || Object.values(colorLibrary)[0];

  // 根据连接状态判断图标类型
  const displayStatus = connectionStatus || t("device.connection.wired");
  const isWireless =
    displayStatus?.includes(t("device.connection.wireless")) ||
    displayStatus?.includes(t("device.connection.bluetooth"));
  const connectionIcon = isWireless ? "wireless" : "wired";

  return (
    <div
      className={`${styles.deviceCard} ${disabled || sleeping ? styles.disabled : ""} ${className || ""}`}
      onClick={disabled || sleeping ? undefined : onClick}
      style={{ position: "relative" }}
    >
      {/* 休眠禁用：底部横条 Zz + 设备休眠 */}
      {sleeping && (
        <div className={styles.sleepOverlay}>
          <Iconfont icon="sleep" />
          <span className={styles.sleepMessage}>
            {t("device.status.sleeping")}
          </span>
        </div>
      )}

      <div className={styles.deviceCardContent}>
        {/* 设备名称 */}
        <h2 className={styles.deviceName}>{deviceName}</h2>

        {/* 设备图片 */}
        <div className={styles.deviceImageWrapper}>
          {deviceImage ? (
            <img
              src={deviceImage}
              alt={deviceName}
              className={styles.deviceImage}
            />
          ) : null}
        </div>

        {/* 颜色选择器 - 自动根据颜色库显示 */}
        {availableColors.length > 0 && (
          <div className={styles.colorSelector}>
            {availableColors.map((color) => (
              <div
                key={color}
                className={`colorDot ${styles.colorDot} ${selectedColor === color ? styles.active : ""}`}
                data-color={color}
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  // 清除之前的定时器
                  if (hoverTimerRef.current) {
                    clearTimeout(hoverTimerRef.current);
                  }
                  // 设置300ms延迟后切换配色
                  hoverTimerRef.current = setTimeout(() => {
                    // 如果没有外部控制，更新内部状态
                    if (externalSelectedColor === undefined) {
                      setInternalSelectedColor(color);
                    }
                    // 触发回调
                    if (onColorChange) {
                      onColorChange(color);
                    }
                  }, 300);
                }}
                onMouseLeave={(e) => {
                  e.stopPropagation();
                  // 离开时清除定时器，防止误触
                  if (hoverTimerRef.current) {
                    clearTimeout(hoverTimerRef.current);
                    hoverTimerRef.current = null;
                  }
                }}
              />
            ))}
          </div>
        )}

        {/* 状态信息 */}

        <div className={styles.deviceStatus}>
          {/* 连接状态 */}
          <div className={styles.statusItem}>
            <Iconfont icon={connectionIcon} />
            <span>{displayStatus}</span>
          </div>

          {batteryLevel !== undefined && batteryLevel !== null && (
            <div className={styles.statusItem}>
              <BatteryLevel
                level={batteryLevel}
                charging={charging}
                showPercentage={true}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { DeviceCard };
